import React, { useRef, useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { 
   Popconfirm, Divider, Card, Select, Input, Button, Space, Typography, Tabs, Switch, 
  InputNumber, Collapse, ColorPicker, Alert, Form, Modal, Checkbox, Tooltip, List, message, Spin
} from 'antd';
import {
  RobotOutlined, DeleteOutlined, SettingOutlined, EyeOutlined, CodeOutlined,
  QuestionCircleOutlined, CrownOutlined, OpenAIOutlined, GoogleOutlined, PlusOutlined, ReloadOutlined, WindowsFilled
} from '@ant-design/icons';
import {
  Anthropic,
  DeepSeek,
  Gemini,
  Grok,
  Meta,
  Mistral,
  OpenAI,
  Perplexity,
  Qwen,
  Nova,
  Nvidia,
  Cohere,
  Moonshot,
  OpenRouter
} from '@lobehub/icons';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';
import ChatPreview from './ChatPreview';
import axios from 'axios';
import config from '../../config';
import { LockedFeature } from '../LockedNodeOverlay';
import { isFeatureLockedInLite } from '../../config/nodeConfig';

const { Option, OptGroup } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;


const ChatNode = ({ id, data, isConnectable, workflowId }) => {
  const [workflowIdState, setWorkflowIdState] = useState(data.workflowId || workflowId);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionForm] = Form.useForm();
  const [vectorStores, setVectorStores] = useState([]);
  const [fetchingVectorStores, setFetchingVectorStores] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  
  // New state for dynamic OpenRouter models
  const [dynamicOpenRouterModels, setDynamicOpenRouterModels] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelContextLengths, setModelContextLengths] = useState({});
  
  const [nodeData, setNodeData] = useState({
    nodeName: data.nodeName || `Chat ${id}`,
    systemPrompt: data.systemPrompt || '',
    workflowId: workflowIdState,
    actions: data.actions || [],
    model: data.model || 'anthropic/claude-3-opus',
    modelParams: data.modelParams || {
      temperature: 1.0,
      top_p: 1.0,
      top_k: 0,
      frequency_penalty: 0.0,
      presence_penalty: 0.0,
      repetition_penalty: 1.0,
      max_tokens: 4096
    },
    design: {
      theme: data.design?.theme || 'light',
      position: data.design?.position || 'bottom-right',
      dimensions: data.design?.dimensions || {
        width: 380,
        height: 600,
        borderRadius: 12
      },
      colors: data.design?.colors || {
        primary: '#1677ff',
        secondary: '#f5f5f5',
        text: '#000000',
        background: '#ffffff'
      },
      font: data.design?.font || {
        family: 'Inter, system-ui, sans-serif',
        size: '14px',
        headerSize: '16px'
      },
      botName: data.design?.botName || 'AI Assistant',
      botIcon: data.design?.botIcon || 'robot',
      quickResponses: Array.isArray(data.design?.quickResponses) ? [...data.design.quickResponses] : [],
      customCSS: data.design?.customCSS || '',
      sendButtonText: data.design?.sendButtonText || 'Send',
      showPoweredBy: data.design?.showPoweredBy !== false
    },
    behavior: {
      initialMessage: data.behavior?.initialMessage || 'Hello! How can I help you today?',
      initialMessageType: data.behavior?.initialMessageType || 'static',
      placeholderText: data.behavior?.placeholderText || 'Type your message here...',
      maxHistoryLength: data.behavior?.maxHistoryLength || 50,
      showTypingIndicator: data.behavior?.showTypingIndicator !== false,
      soundEffects: data.behavior?.soundEffects !== false,
      showCitations: data.behavior?.showCitations || false,
      autoOpenDelay: data.behavior?.autoOpenDelay || 0,
      persistHistory: data.behavior?.persistHistory !== false,
      includePageContext: data.behavior?.includePageContext || false,
      streamResponses: data.behavior?.streamResponses || false,
      rateLimit: data.behavior?.rateLimit || {
        enabled: true,
        maxMessages: 10,
        timeWindow: 60
      }
    },
    openaiTools: {
      webSearch: {
        enabled: data.openaiTools?.webSearch?.enabled || false,
        contextSize: data.openaiTools?.webSearch?.contextSize || 'medium',
        location: data.openaiTools?.webSearch?.location || {
          city: '',
          region: '',
          country: ''
        }
      },
      fileSearch: {
        enabled: data.openaiTools?.fileSearch?.enabled || false,
        vectorStoreId: data.openaiTools?.fileSearch?.vectorStoreId || '',
        maxResults: data.openaiTools?.fileSearch?.maxResults || 5
      }
    }
  });

  const handleContentChange = (nodeId, newData) => {
    // Update local state for immediate UI feedback
    setNodeData(prev => ({
      ...prev,
      systemPrompt: newData.content
    }));
    
    // Update parent state through data.updateNodeData
    data.updateNodeData(nodeId, {
      ...nodeData,
      systemPrompt: newData.content
    });
  };

  const [previewVisible, setPreviewVisible] = useState(false);
  const [openAIModels, setOpenAIModels] = useState([
    { value: "o1", label: "o1 (Reasoning model for hard problems)" },
    { value: "o1-mini", label: "o1-Mini (Fast, cheap reasoning model for coding, math, science)" },
    { value: "o3-mini", label: "o3-Mini (Fast, cheap reasoning model for coding, math, science)" },
    { value: "o3-mini-high", label: "o3-Mini-high (Fast reasoning model for coding, math, science)" },
    { value: "gpt-4o", label: "GPT-4o (High-intelligence flagship model)" },
    { value: "gpt-4o-mini", label: "GPT-4o-mini (Affordable, intelligent small model)" },
  ]);

  // Fetch OpenRouter models on component mount
  useEffect(() => {
    fetchOpenRouterModels();
  }, []);

  useEffect(() => {
    data.updateNodeData(id, nodeData);
  }, [nodeData, id, data]);

  useEffect(() => {
    if (workflowId) {
      setWorkflowIdState(workflowId);
    }
  }, [workflowId]);

  useEffect(() => {
    if (data.workflowId) {
      setWorkflowIdState(data.workflowId);
    }
  }, [data.workflowId]);

  useEffect(() => {
    // Update nodeData when workflowIdState changes
    setNodeData(prev => ({
      ...prev,
      workflowId: workflowIdState
    }));
  }, [workflowIdState]);

  useEffect(() => {
    return () => {
      if (previewVisible) {
        setPreviewVisible(false);
      }
    };
  }, []);

  useEffect(() => {
    if (nodeData.openaiTools?.fileSearch?.enabled) {
      fetchVectorStores();
    }
  }, [nodeData.openaiTools?.fileSearch?.enabled]);
  
  // Function to fetch models from OpenRouter API through WordPress backend
  const fetchOpenRouterModels = async () => {
    if (data.isViewer) {
      return;
    }
    
    setLoadingModels(true);
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/openrouter-models`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      if (response.data && Array.isArray(response.data.data)) {
        // Process the models and group by provider
        const models = response.data.data;
        const groupedModels = {};
        const contextLengths = {};
        
        // Create a dynamic list for direct OpenAI models
        const dynamicOpenAIModels = [];
        
        models.forEach(model => {
          // Extract provider from model ID (e.g., "anthropic/claude-3" -> "Anthropic")
          const providerMatch = model.id.match(/^([^/]+)\//);
          const provider = providerMatch ? 
            providerMatch[1].charAt(0).toUpperCase() + providerMatch[1].slice(1) : 
            "Other";
          
          if (!groupedModels[provider]) {
            groupedModels[provider] = [];
          }
          
          // Store context length for the model
          contextLengths[model.id] = model.context_length || 4096;
          
          // If it's an OpenAI model, also add it to the direct models list
          if (provider.toLowerCase() === 'openai') {
            const directModelId = model.id.split('/')[1]; // Remove the "openai/" prefix
            const directModelExists = dynamicOpenAIModels.some(m => m.value === directModelId);
            
            // Only add if it's not already in the list
            if (!directModelExists) {
              dynamicOpenAIModels.push({
                value: directModelId,
                label: `${model.name || directModelId} (${model.description || 'OpenAI model'})`,
                description: model.description || ''
              });
              
              // Also store context length for direct model
              contextLengths[directModelId] = model.context_length || 4096;
            }
          }
          
          groupedModels[provider].push({
            value: model.id,
            label: model.name || model.id.split('/').pop(),
            description: model.description || ''
          });
        });
        
        // Sort models within each provider
        Object.keys(groupedModels).forEach(provider => {
          groupedModels[provider].sort((a, b) => a.label.localeCompare(b.label));
        });
        
        // Sort direct OpenAI models
        dynamicOpenAIModels.sort((a, b) => a.label.localeCompare(b.label));
        
        setDynamicOpenRouterModels(groupedModels);
        setModelContextLengths(contextLengths);
        
        // Replace the hardcoded OpenAI models with the dynamic ones if we have any
        if (dynamicOpenAIModels.length > 0) {
          setOpenAIModels(dynamicOpenAIModels);
        }
      }
    } catch (error) {
      console.error('Error fetching OpenRouter models:', error);
      // Fallback to hardcoded models in ChatNode.js
      setDynamicOpenRouterModels(modelGroups);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleModelParamUpdate = (param, value) => {
    const constraints = {
      temperature: { min: 0, max: 2 },
      top_p: { min: 0, max: 1 },
      top_k: { min: 0, max: 100 },
      frequency_penalty: { min: -2, max: 2 },
      presence_penalty: { min: -2, max: 2 },
      repetition_penalty: { min: 0, max: 2 },
      max_tokens: { min: 1, max: getMaxTokensForModel(nodeData.model) }
    };

    if (value < constraints[param].min || value > constraints[param].max) {
      message.error(`${param} must be between ${constraints[param].min} and ${constraints[param].max}`);
      return;
    }

    setNodeData(prev => ({
      ...prev,
      modelParams: {
        ...prev.modelParams,
        [param]: value
      }
    }));
  };

  const fetchVectorStores = async () => {
    if (data.isViewer) {
      return;
    }
    
    try {
      setFetchingVectorStores(true);
      
      // Fetch managed vector stores
      const managedResponse = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Fetch OpenAI vector stores
      const openaiResponse = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/openai-vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Process OpenAI stores
      let openaiStores = [];
      if (openaiResponse.data && openaiResponse.data.data) {
        openaiStores = openaiResponse.data.data.map(store => ({
          id: store.id,
          name: `${store.name} (OpenAI)`,
          is_default: false,
          is_openai_store: true
        }));
      }
      
      // Combine the two sets of stores
      const combinedStores = [...managedResponse.data, ...openaiStores];
      setVectorStores(combinedStores);
    } catch (error) {
      console.error('Error fetching vector stores:', error);
      message.error('Failed to fetch vector stores');
    } finally {
      setFetchingVectorStores(false);
    }
  };
  
  const isDirectOpenAIModel = (model) => {
    return openAIModels.some(m => m.value === model);
  };
  
  const handleToolToggle = (toolType, enabled) => {
    setNodeData(prev => ({
      ...prev,
      openaiTools: {
        ...prev.openaiTools,
        [toolType]: {
          ...prev.openaiTools?.[toolType],
          enabled
        }
      }
    }));
  };
  
  const handleToolOptionChange = (toolType, option, value) => {
    setNodeData(prev => ({
      ...prev,
      openaiTools: {
        ...prev.openaiTools,
        [toolType]: {
          ...prev.openaiTools?.[toolType],
          [option]: value
        }
      }
    }));
  };
  
  const handleToolLocationChange = (locationKey, value) => {
    setNodeData(prev => ({
      ...prev,
      openaiTools: {
        ...prev.openaiTools,
        webSearch: {
          ...prev.openaiTools.webSearch,
          location: {
            ...prev.openaiTools.webSearch.location,
            [locationKey]: value
          }
        }
      }
    }));
  };


  const renderShortcodeSection = () => {
    return (
      <div className="shortcode-section" style={{ 
        borderTop: '1px solid #f0f0f0', 
        marginTop: 16, 
        paddingTop: 16 
      }}>
        <Title level={5}>Embed Chat Widget</Title>
        {workflowIdState ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text copyable>{`[wp_ai_workflow_chat id="${workflowIdState}"]`}</Text>
            <Text type="secondary">
              Add this shortcode to any post or page where you want the chat widget to appear. 
              The chat widget will automatically use the latest configuration.
            </Text>
          </Space>
        ) : (
          <Alert
            type="info"
            message="You will see the shortcode to embed chat here after saving the workflow."
            showIcon
          />
        )}
      </div>
    );
  };

  const handleDesignUpdate = (key, value) => {
    setNodeData(prev => {
      // If we're updating quickResponses but it doesn't exist yet
      if (key === 'quickResponses' && !prev.design.quickResponses) {
        return {
          ...prev,
          design: {
            ...prev.design,
            quickResponses: value
          }
        };
      }
      
      // Normal case
      return {
        ...prev,
        design: {
          ...prev.design,
          [key]: value
        }
      };
    });
  };

  const handleBehaviorUpdate = (key, value) => {
    setNodeData(prev => ({
      ...prev,
      behavior: {
        ...prev.behavior,
        [key]: value
      }
    }));
  };

  const getMaxTokensForModel = (model) => {
    // Check if we have context length from dynamically fetched models
    if (modelContextLengths[model]) {
      return modelContextLengths[model];
    }
    
    // Fallback to hardcoded values
    const tokenLimits = {
      'gpt-4o': 16384,
      'gpt-4o-mini': 16384,
      'o1': 100000,
      'o1-mini': 65536,
      'o3-mini': 100000,
      'o3-mini-high': 100000,
      'meta-llama/llama-3.2-11b-vision-instruct:free': 4096,
      'meta-llama/llama-3.2-11b-vision-instruct': 131072,
      'meta-llama/llama-3.3-70b-instruct': 131072,
      'mistralai/mistral-nemo': 128000,
      'mistralai/mistral-large-2411': 128000,
      'mistralai/pixtral-large-2411': 128000,
      'mistralai/mistral-small-3.1-24b-instruct': 128000,
      'google/gemma-2-9b-it:free': 2048,
      'anthropic/claude-3.7-sonnet': 128000,
      'anthropic/claude-3.5-sonnet': 8192,
      'anthropic/claude-3-5-haiku': 8192,
      'anthropic/claude-3-haiku': 4096,
      'anthropic/claude-3-opus': 4096,
      'openai/gpt-4o-search-preview': 16384,
      'openai/gpt-4o-mini-search-preview': 16384,
      'openai/chatgpt-4o-latest': 16384,
      'openai/gpt-4o-mini': 16384,
      'openai/o1': 100000,
      'openai/o1-mini': 65536,
      'openai/o3-mini': 100000,
      'openai/o3-mini-high': 100000,
      'google/gemma-3-12b-it:free': 8192,
      'google/gemma-3-4b-it:free': 8192,
      'google/gemini-flash-8b-1.5-exp': 8192,
      'google/gemini-flash-1.5-8b': 8192,
      'google/gemini-2.0-flash-exp': 8192,
      'x-ai/grok-2-vision-1212': 32768,
      'x-ai/grok-2-1212': 131072,
      'perplexity/sonar':  127072,
      'perplexity/sonar-reasoning':  127072,
      'deepseek/deepseek-r1':  128000,
      'deepseek/deepseek-chat':  131000, 
    };
    return tokenLimits[model] || 4096;
  };

  const getModelIcon = (provider) => {
    const iconSize = 20; // Standard size for all icons
    const normalizedProvider = provider.toLowerCase();
    
    switch (normalizedProvider) {
      case 'openai':
        return <OpenAI size={iconSize} />;
      case 'google':
        return <Gemini size={iconSize} />;
      case 'perplexity':
        return <Perplexity size={iconSize} />;
      case 'mistral':
      case 'mistralai':
        return <Mistral size={iconSize} />;
      case 'meta':
      case 'meta-llama':
      case 'meta llama':
        return <Meta size={iconSize} />;
      case 'anthropic':
        return <Anthropic size={iconSize} />;
      case 'xai':
      case 'x-ai':
      case 'x':
        return <Grok size={iconSize} />;
      case 'deepseek':
        return <DeepSeek size={iconSize} />;
      case 'qwen':
      case 'alibaba':
      case 'aliqwen':
        return <Qwen size={iconSize} />;
      case 'cohere':
        return <Cohere size={iconSize} />;
      case 'nvidia':
        return <Nvidia size={iconSize} />;
      case 'nova':
      case 'anthropic nova':
      case 'novaai':
        return <Nova size={iconSize} />;
      case 'microsoft':
      case 'azure':
        return <WindowsFilled size={iconSize} />;
      case 'moonshot':
      case 'moonshot-ai':
        return <Moonshot size={iconSize} />;
      case 'openrouter':
        return <OpenRouter size={iconSize} />;
      default:
        return <RobotOutlined style={{ fontSize: iconSize }} />;
    }
  };


  const modelGroups = {
    OpenAI: [
      { value: "openai/o1", label: "o1" },
      { value: "openai/o1-mini", label: "o1-Mini" },
      { value: "openai/o3-mini-high", label: "o3 mini high" },
      { value: "openai/o3-mini", label: "o3 mini" },
      { value: "openai/chatgpt-4o-latest", label: "ChatGPT-4o Latest" },
      { value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "openai/gpt-4o-search-preview", label: "GPT-4o with Search (Preview)" },
      { value: "openai/gpt-4o-mini-search-preview", label: "GPT-4o Mini Search (Preview)" },
    ],
    Anthropic: [
      { value: "anthropic/claude-3.7-sonnet", label: "Claude 3.7 Sonnet" },
      { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
      { value: "anthropic/claude-3-5-haiku", label: "Claude 3.5 Haiku" },
      { value: "anthropic/claude-3-opus", label: "Claude 3 Opus" },
      { value: "anthropic/claude-3-haiku", label: "Claude 3 Haiku" },
    ],

    "Meta Llama": [
      { value: "meta-llama/llama-3.3-70b-instruct", label: "Llama 3.3 70B Instruct" },
      { value: "meta-llama/llama-3.2-3b-instruct", label: "Llama 3.2 3B Instruct" },
      { value: "meta-llama/llama-3.2-1b-instruct", label: "Llama 3.2 1B Instruct" },
      { value: "meta-llama/llama-3.2-90b-vision-instruct", label: "Llama 3.2 90B Vision Instruct" },
      { value: "meta-llama/llama-3.2-11b-vision-instruct:free", label: "Llama 3.2 11B Vision Instruct (Free)" },
    ],

    Google: [
      { value: "google/gemma-3-12b-it:free", label: "Gemma 3 12B (Free)" },
      { value: "google/gemma-3-4b-it:free", label: "Gemma 3 4B (Free)" },
      { value: "google/gemini-2.0-flash-exp:free", label: "Gemini 2.0 Flash Experimental (free)" },
      { value: "google/gemini-flash-8b-1.5-exp", label: "Gemini Flash 8B 1.5 Exp" },
      { value: "google/gemini-flash-1.5-8b", label: "Gemini Flash 1.5" },
      { value: "google/gemma-2-9b-it:free", label: "Gemma 2 9B IT (Free)" },
    ],
    Perplexity: [
      { value: "perplexity/sonar", label: "Perplexity Sonar" },
      { value: "perplexity/sonar-reasoning", label: "Perplexity Sonar Reasoning" },
    ],
    Mistral: [
      { value: "mistralai/mistral-small-3.1-24b-instruct", label: "Mistral Small 3.1 24B" },
      { value: "mistralai/pixtral-large-2411", label: "Pixtral 2" },
      { value: "mistralai/mistral-large-2411", label: "Mistral Large 2" },
      { value: "mistralai/mistral-nemo", label: "Mistral Nemo" },
    ],
    xAI: [
      { value: "x-ai/grok-2-1212", label: "Grok 2" },
      { value: "x-ai/grok-2-vision-1212", label: "Grok 2 Vision" },
    ],
    DeepSeek: [
      { value: "deepseek/deepseek-r1", label: "DeepSeek R1" },
      { value: "deepseek/deepseek-chat", label: "DeepSeek V3" },
    ]
  };


  const handleAddAction = (values) => {
    // First, disable streaming if it's currently enabled
    if (nodeData.behavior.streamResponses) {
      setNodeData(prev => ({
        ...prev,
        behavior: {
          ...prev.behavior,
          streamResponses: false
        }
      }));
      message.info('Streaming responses has been disabled because actions are being used.');
    }
  
    if (editingActionId) {
      // Update existing action
      setNodeData(prev => ({
        ...prev,
        actions: prev.actions.map(action => 
          action.id === editingActionId ? {
            ...action,
            name: values.name,
            description: values.description,
            fields: values.fields.map(field => ({
              name: field.name,
              type: field.type,
              required: field.required
            }))
          } : action
        )
      }));
      setEditingActionId(null);
    } else {
      // Add new action
      const newAction = {
        id: `action-${Date.now()}`,
        name: values.name,
        description: values.description,
        fields: values.fields.map(field => ({
          name: field.name,
          type: field.type,
          required: field.required
        }))
      };
      
      setNodeData(prev => ({
        ...prev,
        actions: [...prev.actions, newAction]
      }));
    }
  
    actionForm.resetFields();
    setActionModalVisible(false);
  };

  const handleStreamingToggle = (checked) => {
    if (checked && nodeData.actions.length > 0) {
      message.warning('Cannot enable streaming responses while actions are configured. Please remove all actions first.');
      return;
    }
    
    handleBehaviorUpdate('streamResponses', checked);
  };

  const handleModalCancel = () => {
    setEditingActionId(null);
    actionForm.resetFields();
    setActionModalVisible(false);
  };

  const handleEditAction = (action) => {
    setEditingActionId(action.id);
    actionForm.setFieldsValue({
      name: action.name,
      description: action.description,
      fields: action.fields
    });
    setActionModalVisible(true);
  };

  const handleDeleteAction = (actionId) => {
    const updatedActions = nodeData.actions.filter(action => action.id !== actionId);
    
    setNodeData(prev => ({
      ...prev,
      actions: updatedActions
    }));
  
    // No need to filter edges or call onDeleteEdge - just pass it to updateNodeData
    data.updateNodeData(id, {
      actions: updatedActions
    });
    
    // Show message that streaming is now available if this was the last action
    if (updatedActions.length === 0) {
      message.info('All actions removed. Streaming responses can now be enabled.');
    }
  };




  return (
    <>
      <Card title="Chatbot" style={{ width: 450 }} extra={
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>Preview</Button>
          <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }>
        <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
  
        <Input
          placeholder="Node Name"
          value={nodeData.nodeName}
          onChange={(e) => setNodeData(prev => ({ ...prev, nodeName: e.target.value }))}
          style={{ marginBottom: 8 }}
          className="nodrag"
        />
  
        <Tabs defaultActiveKey="model">
          <TabPane tab="Model" key="model" className="nodrag">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Select AI Model</Text>
                <Button 
                  icon={<ReloadOutlined />} 
                  size="small"
                  onClick={fetchOpenRouterModels}
                  loading={loadingModels}
                  type="text"
                />
              </div>
            
              <Select
                value={nodeData.model}
                onChange={(value) => setNodeData(prev => ({ ...prev, model: value }))}
                style={{ width: '100%' }}
                className="nodrag"
                showSearch
                filterOption={(input, option) => {
                  // Safely handle string or object labels
                  const searchText = option.label || option['data-label'] || '';
                  const searchValue = typeof searchText === 'string' ? searchText : '';
                  return searchValue.toLowerCase().includes(input.toLowerCase());
                }}
                loading={loadingModels}
                placeholder={loadingModels ? "Loading models..." : "Select AI model"}
                notFoundContent={loadingModels ? <Spin size="small" /> : null}
              >
                <OptGroup label="OpenAI (Direct)">
                  {openAIModels.map(model => (
                    <Option key={model.value} value={model.value} data-label={model.label}>
                      <Space>
                        <OpenAIOutlined />
                        <Text>{model.label}</Text>
                      </Space>
                    </Option>
                  ))}
                </OptGroup>
                
                {/* Dynamic OpenRouter Models */}
                {Object.keys(dynamicOpenRouterModels).length > 0 ? (
                  Object.entries(dynamicOpenRouterModels).map(([provider, models]) => (
                    <OptGroup 
                      key={provider} 
                      label={
                        <Space>
                          {getModelIcon(provider)}
                          <span>{provider} (via OpenRouter)</span>
                        </Space>
                      }
                    >
                      {models.map(model => (
                        <Option key={model.value} value={model.value} data-label={model.label}>
                          <Space>
                            {getModelIcon(provider)}
                            <Text>{model.label}</Text>
                            {model.description && (
                              <Tooltip title={model.description}>
                                <QuestionCircleOutlined style={{ color: '#bfbfbf' }} />
                              </Tooltip>
                            )}
                          </Space>
                        </Option>
                      ))}
                    </OptGroup>
                  ))
                ) : (
                  // Fallback to hardcoded models if dynamic fetch fails
                  Object.entries(modelGroups).map(([provider, models]) => (
                    <OptGroup key={provider} label={
                      <Space>
                        {getModelIcon(provider)}
                        <span>{provider}</span>
                      </Space>
                    }>
                      {models.map(model => (
                        <Option key={model.value} value={model.value} data-label={model.label}>
                          <Space>
                            {getModelIcon(provider)}
                            <Text>{model.label}</Text>
                          </Space>
                        </Option>
                      ))}
                    </OptGroup>
                  ))
                )}
              </Select>

              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <Text strong>Built-in OpenAI Tools</Text>
                <Alert
                  style={{ marginTop: 8, marginBottom: 8 }}
                  message="These tools are only available for direct OpenAI API calls"
                  type="info"
                  showIcon
                />
                <div style={{ marginTop: 12 }}>
                <Checkbox 
                  checked={nodeData.openaiTools?.webSearch?.enabled || false}
                  onChange={(e) => handleToolToggle('webSearch', e.target.checked)}
                  disabled={!isDirectOpenAIModel(nodeData.model)}
                >
                  Enable Web Search
                </Checkbox>
                  
                  {nodeData.openaiTools?.webSearch?.enabled && (
                    <div style={{ marginLeft: 24, marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Search Context Size</Text>
                        <Tooltip title="Controls how much context is retrieved from the web. Higher values provide better results but cost more.">
                          <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                        <Select
                          style={{ width: '100%', marginTop: 4 }}
                          value={nodeData.openaiTools?.webSearch?.contextSize || 'medium'}
                          onChange={(value) => handleToolOptionChange('webSearch', 'contextSize', value)}
                        >
                          <Option value="low">Low (fastest, cheapest)</Option>
                          <Option value="medium">Medium (balanced)</Option>
                          <Option value="high">High (most comprehensive)</Option>
                        </Select>
                      </div>
                      
                      <Collapse ghost>
                        <Panel header="Location Settings (Optional)" key="location">
                          <Text type="secondary">Customize search results based on geography</Text>
                          <Input 
                            placeholder="City (e.g., New York)"
                            value={nodeData.openaiTools?.webSearch?.location?.city || ''}
                            onChange={(e) => handleToolLocationChange('city', e.target.value)}
                            style={{ marginTop: 8 }}
                          />
                          <Input 
                            placeholder="Region/State (e.g., New York)"
                            value={nodeData.openaiTools?.webSearch?.location?.region || ''}
                            onChange={(e) => handleToolLocationChange('region', e.target.value)}
                            style={{ marginTop: 8 }}
                          />
                          <Input 
                            placeholder="Country Code (e.g., US)"
                            value={nodeData.openaiTools?.webSearch?.location?.country || ''}
                            onChange={(e) => handleToolLocationChange('country', e.target.value)}
                            maxLength={2}
                            style={{ marginTop: 8 }}
                          />
                        </Panel>
                      </Collapse>
                    </div>
                  )}
                </div>
                
                <div style={{ marginTop: 12 }}>
                <Checkbox 
                  checked={nodeData.openaiTools?.fileSearch?.enabled || false}
                  onChange={(e) => handleToolToggle('fileSearch', e.target.checked)}
                  disabled={!isDirectOpenAIModel(nodeData.model)}
                >
                  Enable File Search
                </Checkbox>
                  
                  {nodeData.openaiTools?.fileSearch?.enabled && (
                    <div style={{ marginLeft: 24, marginTop: 8 }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Vector Store</Text>
                        <Tooltip title="Select a vector store to search for relevant information.">
                          <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                        <Select
                          style={{ width: '100%', marginTop: 4 }}
                          value={nodeData.openaiTools?.fileSearch?.vectorStoreId || ''}
                          onChange={(value) => handleToolOptionChange('fileSearch', 'vectorStoreId', value)}
                          placeholder="Select a vector store"
                          loading={fetchingVectorStores}
                        >
                          {vectorStores.map(store => (
                            <Option key={store.id} value={store.id}>
                              {store.name} {store.is_default && '(Default)'}
                            </Option>
                          ))}
                        </Select>
                        {vectorStores.length === 0 && !fetchingVectorStores && (
                          <Alert
                            type="warning"
                            message="No vector stores found"
                            description="Create vector stores in the settings to use file search."
                            showIcon
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </div>
                      
                      <div style={{ marginBottom: 8 }}>
                        <Text>Max Results</Text>
                        <Tooltip title="Maximum number of search results to return. Lower values use fewer tokens.">
                          <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                        <InputNumber
                          min={1}
                          max={20}
                          style={{ width: '100%', marginTop: 4 }}
                          value={nodeData.openaiTools?.fileSearch?.maxResults || 5}
                          onChange={(value) => handleToolOptionChange('fileSearch', 'maxResults', value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
  
              <Collapse ghost>
                <Panel header="Model Parameters" key="params">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Temperature</Text>
                      <Tooltip title="Controls response randomness">
                        <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                      </Tooltip>
                      <InputNumber
                        min={0}
                        max={2}
                        step={0.1}
                        value={nodeData.modelParams.temperature}
                        onChange={(value) => handleModelParamUpdate('temperature', value)}
                        style={{ width: '100%' }}
                      />
                    </div>
  
                    <div>
                      <Text>Top P</Text>
                      <Tooltip title="Controls token sampling diversity">
                        <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                      </Tooltip>
                      <InputNumber
                        min={0}
                        max={1}
                        step={0.1}
                        value={nodeData.modelParams.top_p}
                        onChange={(value) => handleModelParamUpdate('top_p', value)}
                        style={{ width: '100%' }}
                      />
                    </div>
  
                    <div>
                      <Text>Max Tokens</Text>
                      <Tooltip title="Maximum response length">
                        <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                      </Tooltip>
                      <InputNumber
                        min={1}
                        max={getMaxTokensForModel(nodeData.model)}
                        value={nodeData.modelParams.max_tokens}
                        onChange={(value) => handleModelParamUpdate('max_tokens', value)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Space>
                </Panel>
              </Collapse>
            </Space>
          </TabPane>
  
          <TabPane tab="Prompt" key="knowledge" className="nodrag">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>System Prompt</Text>
            <GenericNodeContent 
              id={id} 
              data={{ 
                content: nodeData.systemPrompt,
                updateNodeData: data.updateNodeData 
              }} 
              onChange={(nodeId, newData) => {
                // Update local state for immediate UI feedback
                setNodeData(prev => ({
                  ...prev,
                  systemPrompt: newData.content
                }));
              }}
              placeholder="Enter base instructions for the chatbot. Here you can add data from previous nodes for be used as knowledge for the chatbot."
              nodes={data.nodes || []}
              className="nodrag"
            />
            <Alert
              type="info"
              message="Connect nodes to use their output as knowledge base for the chatbot"
              showIcon
            />
          </Space>
          </TabPane>

<TabPane tab="Actions" key="actions">
  <Space direction="vertical" style={{ width: '100%' }}>
    {process.env.REACT_APP_VERSION === 'lite' && isFeatureLockedInLite('chat', 'actions') ? (
      <div style={{ 
        background: '#f0f8ff', 
        padding: 16, 
        borderRadius: 6,
        border: '1px dashed #1890ff',
        textAlign: 'center',
        marginBottom: 16
      }}>
        <CrownOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
          Advanced Chat Actions
        </div>
        <div style={{ marginBottom: 12, color: '#666' }}>
          🔒 Automated actions are available in Pro version
        </div>
        <Button 
          type="primary" 
          size="small"
          onClick={() => window.open('https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite', '_blank')}
        >
          Upgrade to Pro
        </Button>
      </div>
    ) : (
      <>
        {nodeData.behavior.streamResponses && (
          <Alert
            message="Actions cannot be used with streaming responses"
            description="Disable streaming responses in the Behavior tab before adding actions."
            type="warning"
            showIcon
          />
        )}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Automated Actions</Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setActionModalVisible(true)}
            disabled={nodeData.behavior.streamResponses}
          >
            Add Action
          </Button>
        </div>

        {nodeData.actions.length === 0 ? (
          <Alert
            message="No actions configured"
            description="Add actions to handle specific user intents automatically."
            type="info"
            showIcon
          />
        ) : (
          <List
            dataSource={nodeData.actions}
            renderItem={action => (
              <List.Item
                actions={[
                  <Tooltip title="Edit action" key="edit">
                    <Button 
                      icon={<SettingOutlined />} 
                      onClick={() => handleEditAction(action)}
                    />
                  </Tooltip>,
                  <Tooltip title="Delete action" key="delete">
                    <Popconfirm
                      title="Delete Action"
                      description="Are you sure you want to delete this action?"
                      onConfirm={() => handleDeleteAction(action.id)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button 
                        icon={<DeleteOutlined />} 
                        danger
                      />
                    </Popconfirm>
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  title={action.name}
                  description={
                    <Collapse ghost>
                      <Panel header="Details" key="1">
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Text type="secondary">Description: {action.description}</Text>
                          <Text type="secondary">Required Fields:</Text>
                          <ul>
                            {action.fields.map(field => (
                              <li key={field.name}>
                                {field.name} ({field.type})
                                {field.required && ' *'}
                              </li>
                            ))}
                          </ul>
                        </Space>
                      </Panel>
                    </Collapse>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </>
    )}
  </Space>
</TabPane>
  
          <TabPane tab="Design" key="design" className="nodrag">
          <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                  style={{ width: '100%' }}
                  value={nodeData.design.theme}
                  onChange={(value) => handleDesignUpdate('theme', value)}
              >
                  <Option value="light">Light Theme</Option>
                  <Option value="dark">Dark Theme</Option>
                  <Option value="custom">Custom Theme</Option>
              </Select>

              <Select
                style={{ width: '100%' }}
                value={nodeData.design.position}
                onChange={(value) => handleDesignUpdate('position', value)}
              >
                <Option value="bottom-right">Bottom Right</Option>
                <Option value="bottom-left">Bottom Left</Option>
                <Option value="top-right">Top Right</Option>
                <Option value="top-left">Top Left</Option>
                <Option value="inline">Inline</Option>
              </Select>

              <Collapse ghost>
                  <Panel header="Bot Identity" key="identity">
                      <Space direction="vertical" style={{ width: '100%' }}>
                          <Input
                              prefix={<RobotOutlined />}
                              placeholder="Bot Name"
                              value={nodeData.design.botName}
                              onChange={(e) => handleDesignUpdate('botName', e.target.value)}
                          />
                          
                          <Select
                              style={{ width: '100%' }}
                              value={nodeData.design.botIcon}
                              onChange={(value) => handleDesignUpdate('botIcon', value)}
                          >
                              <Option value="robot">Robot</Option>
                              <Option value="assistant">Assistant</Option>
                              <Option value="brain">Brain</Option>
                              <Option value="chat">Chat</Option>
                          </Select>
                      </Space>
                  </Panel>

                  <Panel header="Typography" key="typography">
                      <Space direction="vertical" style={{ width: '100%' }}>
                          <Select
                              style={{ width: '100%' }}
                              value={nodeData.design.font.family}
                              onChange={(value) => handleDesignUpdate('font', { ...nodeData.design.font, family: value })}
                          >
                              <Option value="Inter, system-ui, sans-serif">Inter</Option>
                              <Option value="Arial, sans-serif">Arial</Option>
                              <Option value="Georgia, serif">Georgia</Option>
                              <Option value="Montserrat, sans-serif">Montserrat</Option>
                              <Option value="Roboto, sans-serif">Roboto</Option>
                          </Select>

                          <Space>
                              <div>
                                  <Text>Chat Font Size</Text>
                                  <InputNumber
                                      min={12}
                                      max={20}
                                      value={parseInt(nodeData.design.font.size)}
                                      onChange={(value) => handleDesignUpdate('font', { 
                                          ...nodeData.design.font, 
                                          size: `${value}px` 
                                      })}
                                      addonAfter="px"
                                  />
                              </div>
                              <div>
                                  <Text>Header Font Size</Text>
                                  <InputNumber
                                      min={14}
                                      max={24}
                                      value={parseInt(nodeData.design.font.headerSize)}
                                      onChange={(value) => handleDesignUpdate('font', { 
                                          ...nodeData.design.font, 
                                          headerSize: `${value}px` 
                                      })}
                                      addonAfter="px"
                                  />
                              </div>
                          </Space>
                      </Space>
                  </Panel>
                  <Panel header="Button Customization" key="buttons">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Send Button Text</Text>
                        <Tooltip title="Customize the text shown on the send button. Use icon codes like &#x2192; for arrows.">
                          <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                        </Tooltip>
                        <Input
                          value={nodeData.design.sendButtonText || 'Send'}
                          onChange={(e) => handleDesignUpdate('sendButtonText', e.target.value)}
                          placeholder="Send"
                        />
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary">
                            Examples: Send, Submit, Go, →, ✓
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Panel>
                <Panel header="Dimensions" key="dimensions">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Width (px)</Text>
                      <InputNumber
                        min={300}
                        max={800}
                        value={nodeData.design.dimensions.width}
                        onChange={(value) => handleDesignUpdate('dimensions', { 
                          ...nodeData.design.dimensions, 
                          width: value 
                        })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text>Height (px)</Text>
                      <InputNumber
                        min={400}
                        max={800}
                        value={nodeData.design.dimensions.height}
                        onChange={(value) => handleDesignUpdate('dimensions', { 
                          ...nodeData.design.dimensions, 
                          height: value 
                        })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text>Border Radius (px)</Text>
                      <InputNumber
                        min={0}
                        max={24}
                        value={nodeData.design.dimensions.borderRadius}
                        onChange={(value) => handleDesignUpdate('dimensions', { 
                          ...nodeData.design.dimensions, 
                          borderRadius: value 
                        })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Space>
                </Panel>
  
                {nodeData.design.theme === 'custom' && (
                  <Panel header="Colors" key="colors">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Primary Color</Text>
                        <ColorPicker
                          value={nodeData.design.colors.primary}
                          onChange={(value) => handleDesignUpdate('colors', { 
                            ...nodeData.design.colors, 
                            primary: value.toHexString() 
                          })}
                        />
                      </div>
                      <div>
                        <Text>Background Color</Text>
                        <ColorPicker
                          value={nodeData.design.colors.background}
                          onChange={(value) => handleDesignUpdate('colors', { 
                            ...nodeData.design.colors, 
                            background: value.toHexString() 
                          })}
                        />
                      </div>
                      <div>
                        <Text>Text Color</Text>
                        <ColorPicker
                          value={nodeData.design.colors.text}
                          onChange={(value) => handleDesignUpdate('colors', { 
                            ...nodeData.design.colors, 
                            text: value.toHexString() 
                          })}
                        />
                      </div>
                    </Space>
                  </Panel>
              
                )}
                <Panel header="Quick Response Buttons" key="quickResponses">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>Quick Response Options</Text>
                      <Button 
                        type="primary" 
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const currentResponses = Array.isArray(nodeData.design.quickResponses) 
                          ? nodeData.design.quickResponses 
                          : [];
                          
                          const newQuickResponses = [
                            ...currentResponses, 
                            { 
                              id: Date.now().toString(), 
                              text: 'This is the label of your button', 
                              message: 'This is the message that is sent to the bot'
                            }
                          ];
                          
                          handleDesignUpdate('quickResponses', newQuickResponses);
                        }}
                      >
                        Add Button
                      </Button>
                    </div>
                    
                    {(nodeData.design.quickResponses || []).length === 0 ? (
                      <Alert
                        message="No quick response buttons configured"
                        description="Add buttons to give users predefined options to click on."
                        type="info"
                        showIcon
                      />
                    ) : (
                      <List
                        dataSource={Array.isArray(nodeData.design.quickResponses) ? nodeData.design.quickResponses : []}
                        renderItem={(item, index) => (
                          <List.Item
                            style={{ padding: '8px 0' }}
                            actions={[
                              <Button 
                                icon={<DeleteOutlined />} 
                                danger 
                                size="small"
                                onClick={() => {
                                  const newQuickResponses = nodeData.design.quickResponses.filter(
                                    (_, i) => i !== index
                                  );
                                  handleDesignUpdate('quickResponses', newQuickResponses);
                                }}
                              />
                            ]}
                          >
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Input
                                  placeholder="Button Text"
                                  value={item.text}
                                  onChange={(e) => {
                                    const newQuickResponses = [...nodeData.design.quickResponses];
                                    newQuickResponses[index].text = e.target.value;
                                    handleDesignUpdate('quickResponses', newQuickResponses);
                                  }}
                                  style={{ flex: 1 }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Input
                                  placeholder="Message to Send"
                                  value={item.message}
                                  onChange={(e) => {
                                    const newQuickResponses = [...nodeData.design.quickResponses];
                                    newQuickResponses[index].message = e.target.value;
                                    handleDesignUpdate('quickResponses', newQuickResponses);
                                  }}
                                  style={{ flex: 1 }}
                                />
                              </div>
                            </Space>
                          </List.Item>
                        )}
                      />
                    )}
                    
                    <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
                      Quick response buttons appear at the beginning of the chat or after AI responses, providing users with convenient options.
                    </Text>
                  </Space>
                </Panel>
                 <Panel header="Footer Options" key="footerOptions">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {process.env.REACT_APP_VERSION === 'lite' && isFeatureLockedInLite('chat', 'poweredByMenu') ? (
                      <div style={{ 
                        background: '#f0f8ff', 
                        padding: 12, 
                        borderRadius: 6,
                        border: '1px dashed #1890ff',
                        textAlign: 'center'
                      }}>
                        <CrownOutlined style={{ fontSize: 20, color: '#1890ff', marginBottom: 6 }} />
                        <div style={{ marginBottom: 6, fontWeight: 'bold', fontSize: '13px' }}>
                          Footer Customization
                        </div>
                        <div style={{ marginBottom: 8, color: '#666', fontSize: '12px' }}>
                          🔒 Remove "Powered by" footer with Pro
                        </div>
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => window.open('https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite', '_blank')}
                        >
                          Upgrade to Pro
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Space align="center">
                          <Checkbox
                            checked={nodeData.design.showPoweredBy !== false}
                            onChange={(e) => handleDesignUpdate('showPoweredBy', e.target.checked)}
                          >
                            Show "Powered by" Footer
                          </Checkbox>
                          <Tooltip title="Display 'Powered by AI Workflow Automation Plugin' in the chat footer">
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </Space>
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">
                            When enabled, a small "Powered by" text appears at the bottom of the chat widget.
                          </Text>
                        </div>
                      </div>
                    )}
                  </Space>
                </Panel>
              </Collapse>
            </Space>
          </TabPane>
  
          <TabPane tab="Behavior" key="behavior" className="nodrag">
            <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Initial Message Type</Text>
              <Tooltip title="Choose between a fixed message or a dynamically generated message based on page context">
                <QuestionCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
              <Select
                value={nodeData.behavior.initialMessageType || 'static'}
                onChange={(value) => handleBehaviorUpdate('initialMessageType', value)}
                style={{ width: '100%', marginBottom: '12px' }}
              >
                <Option value="static">Static (Fixed Message)</Option>
                <Option value="dynamic">Dynamic (AI Generated)</Option>
              </Select>
            </div>

            {(nodeData.behavior.initialMessageType !== 'dynamic') && (
              <div>
                <Text strong>Initial Message</Text>
                <Tooltip title="The first message users see when starting a chat. Sets the tone and helps guide the conversation.">
                  <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
                <Input
                  value={nodeData.behavior.initialMessage}
                  onChange={(e) => handleBehaviorUpdate('initialMessage', e.target.value)}
                  placeholder="Welcome! How can I help you today?"
                />
              </div>
            )}

            {nodeData.behavior.initialMessageType === 'dynamic' && (
              <Alert
                style={{ marginTop: 8, marginBottom: 12 }}
                message="Dynamic Initial Message"
                description="The AI will generate a personalized greeting based on the page context when chat is opened. This uses one API call."
                type="info"
                showIcon
              />
            )}

                <div>
                  <Space align="center">
                    <Text>Stream Responses</Text>
                    <Tooltip title={nodeData.actions.length > 0 ? 
                      "Not available when using actions. Remove all actions to enable streaming." : 
                      "Show AI responses as they're being generated in real-time. Creates a more interactive chat experience."
                    }>
                      <QuestionCircleOutlined />
                    </Tooltip>
                    <Switch
                      checked={nodeData.behavior.streamResponses}
                      onChange={handleStreamingToggle}
                      disabled={nodeData.actions.length > 0}
                    />
                    {nodeData.actions.length > 0 && (
                      <Text type="warning" style={{ fontSize: '12px' }}>
                        (Incompatible with actions)
                      </Text>
                    )}
                  </Space>
                  {nodeData.behavior.streamResponses && (
                    <Alert
                      style={{ marginTop: 8 }}
                      message="Note: Streaming cannot be used with actions"
                      description="If you need to use actions, please disable streaming responses."
                      type="info"
                      showIcon
                    />
                  )}
                </div>

                <Collapse ghost>
                    <Panel header="History & Rate Limiting" key="limits">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Text>Max History Length</Text>
                                <Tooltip title="Maximum number of messages to keep in the chat history. Older messages will be removed when this limit is reached.">
                                    <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                                </Tooltip>
                                <InputNumber
                                    min={10}
                                    max={100}
                                    value={nodeData.behavior.maxHistoryLength}
                                    onChange={(value) => handleBehaviorUpdate('maxHistoryLength', value)}
                                />
                            </div>

                            <div>
                                <Space align="center">
                                    <Text>Enable Rate Limiting</Text>
                                    <Tooltip title="Prevent abuse by limiting the number of messages a user can send within a time window.">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                    <Switch
                                        checked={nodeData.behavior.rateLimit.enabled}
                                        onChange={(checked) => handleBehaviorUpdate('rateLimit', {
                                            ...nodeData.behavior.rateLimit,
                                            enabled: checked
                                        })}
                                    />
                                </Space>
                            </div>

                            {nodeData.behavior.rateLimit.enabled && (
                                <>
                                    <div>
                                        <Text>Max Messages</Text>
                                        <Tooltip title="Maximum number of messages allowed within the time window.">
                                            <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                        <InputNumber
                                            min={1}
                                            max={100}
                                            value={nodeData.behavior.rateLimit.maxMessages}
                                            onChange={(value) => handleBehaviorUpdate('rateLimit', {
                                                ...nodeData.behavior.rateLimit,
                                                maxMessages: value
                                            })}
                                        />
                                    </div>

                                    <div>
                                        <Text>Time Window (seconds)</Text>
                                        <Tooltip title="Time period (in seconds) for the rate limit. For example, 10 messages per 60 seconds.">
                                            <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                        <InputNumber
                                            min={10}
                                            max={3600}
                                            value={nodeData.behavior.rateLimit.timeWindow}
                                            onChange={(value) => handleBehaviorUpdate('rateLimit', {
                                                ...nodeData.behavior.rateLimit,
                                                timeWindow: value
                                            })}
                                        />
                                    </div>
                                </>
                            )}
                        </Space>
                    </Panel>

                    <Panel header="Advanced Settings" key="advanced">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Space align="center">
                                    <Text>Typing Indicator</Text>
                                    <Tooltip title="Show animated dots while the AI is generating a response, providing visual feedback to users.">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                    <Switch
                                        checked={nodeData.behavior.showTypingIndicator}
                                        onChange={(checked) => handleBehaviorUpdate('showTypingIndicator', checked)}
                                    />
                                </Space>
                            </div>

                            <div>
                              <Space align="center">
                                <Text>Page Context Awareness</Text>
                                <Tooltip title="When enabled, the chatbot will be aware of the current page the user is viewing, including the page title, URL, and content summary.">
                                  <QuestionCircleOutlined />
                                </Tooltip>
                                <Switch
                                  checked={nodeData.behavior.includePageContext}
                                  onChange={(checked) => handleBehaviorUpdate('includePageContext', checked)}
                                />
                              </Space>
                            </div>
                            <div style={{ paddingLeft: '24px', display: nodeData.behavior.includePageContext ? 'block' : 'none' }}>
                              <Alert
                                type="info"
                                message="With this option enabled, the chatbot will receive information about the page where it's embedded, helping it provide more contextually relevant responses."
                                showIcon
                                style={{ marginBottom: '8px' }}
                              />
                            </div>

                            <div>
                                <Space align="center">
                                    <Text>Sound Effects</Text>
                                    <Tooltip title="Play subtle sounds when sending and receiving messages for better user engagement.">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                    <Switch
                                        checked={nodeData.behavior.soundEffects}
                                        onChange={(checked) => handleBehaviorUpdate('soundEffects', checked)}
                                    />
                                </Space>
                            </div>

                            <div>
                                <Text>Auto Open Delay (seconds)</Text>
                                <Tooltip title="Automatically open the chat widget after this many seconds. Set to 0 to disable auto-open.">
                                    <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                                </Tooltip>
                                <InputNumber
                                    min={0}
                                    max={60}
                                    value={nodeData.behavior.autoOpenDelay}
                                    onChange={(value) => handleBehaviorUpdate('autoOpenDelay', value)}
                                />
                            </div>

                            <div>
                                <Space align="center">
                                    <Text>Persist History</Text>
                                    <Tooltip title="Save chat history in browser storage so conversations continue across page reloads.">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                    <Switch
                                        checked={nodeData.behavior.persistHistory}
                                        onChange={(checked) => handleBehaviorUpdate('persistHistory', checked)}
                                    />
                                </Space>
                            </div>
                            <div>
                              <Space align="center">
                                <Text>Show Citations</Text>
                                <Tooltip title="When enabled, shows source citations for responses that include web search or file search results (when using OpenAI Tools).">
                                  <QuestionCircleOutlined />
                                </Tooltip>
                                <Switch
                                  checked={nodeData.behavior.showCitations}
                                  onChange={(checked) => handleBehaviorUpdate('showCitations', checked)}
                                />
                              </Space>
                            </div>
                        </Space>
                    </Panel>
                </Collapse>
            </Space>
        </TabPane>

        </Tabs>
        {renderShortcodeSection()}

        {/* chat output handle */}
        {nodeData.actions.map((action, index) => (
          <Handle
            key={action.id}
            type="source"
            position={Position.Right}
            id={action.id}
            style={{
              top: `${((index + 1) * (100 / (nodeData.actions.length + 1)))}%`,
              right: -4,
              background: '#52c41a',
              width: 12,
              height: 12
            }}
          >
            {/* Label for the handle */}
            <div
              style={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%) rotate(-90deg)',
                transformOrigin: 'right center',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                background: 'rgba(255,255,255,0.9)',
                padding: '2px 4px',
                borderRadius: '3px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {action.name}
            </div>
          </Handle>
        ))}
      </Card>
  
      <ChatPreview
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        design={nodeData.design}
        behavior={nodeData.behavior}
        model={nodeData.model}
        modelParams={nodeData.modelParams}
        systemPrompt={nodeData.systemPrompt}
      />

      <Modal
        title={editingActionId ? "Edit Action" : "Add Action"}
        open={actionModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        >
          <Form
            form={actionForm}
            onFinish={handleAddAction}
            layout="vertical"
          >
            <Form.Item
              name="name"
              label="Action Name"
              rules={[{ required: true, message: 'Please enter action name' }]}
            >
              <Input placeholder="e.g., Newsletter Subscription" />
            </Form.Item>
        
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea placeholder="What does this action do?" />
            </Form.Item>
        
            <Form.List name="fields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        rules={[{ required: true, message: 'Missing field name' }]}
                      >
                        <Input placeholder="Field Name" />
                      </Form.Item>
        
                      <Form.Item
                        {...restField}
                        name={[name, 'type']}
                        rules={[{ required: true, message: 'Missing field type' }]}
                      >
                        <Select style={{ width: 120 }}>
                          <Select.Option value="text">Text</Select.Option>
                          <Select.Option value="email">Email</Select.Option>
                          <Select.Option value="number">Number</Select.Option>
                          <Select.Option value="phone">Phone</Select.Option>
                        </Select>
                      </Form.Item>
        
                      <Form.Item
                        {...restField}
                        name={[name, 'required']}
                        valuePropName="checked"
                      >
                        <Checkbox>Required</Checkbox>
                      </Form.Item>
        
                      <DeleteOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
        
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Field
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
        
            <Form.Item>
            <Button type="primary" htmlType="submit">
            {editingActionId ? "Update Action" : "Add Action"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>
    </>
  );
};

export default ChatNode;